import React, { useState, useEffect, useCallback } from 'react';
import { 
  User,
  Mail,
  FolderOpen,
  RefreshCw,
  FileText,
  Download,
  Eye,
  Clock,
  File,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';

interface SubmittedProject {
  id: number;
  title: string;
  description: string;
  message: string;
  files: any[];
  files_count: number;
  status: string;
  status_display: string;
  status_color: string;
  user_name: string;
  user_email: string;
  admin_response: string;
  created_at: string;
}

const SubmittedProjectsAdminTab: React.FC = () => {
  const [projects, setProjects] = useState<SubmittedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const formatDate = (dateString: string | null | undefined) => {
    try {
      if (!dateString || dateString === '') {
        return 'Date non disponible';
      }
      const cleanedDate = String(dateString).trim();
      const date = new Date(cleanedDate);
      if (isNaN(date.getTime())) {
        return 'Date non disponible';
      }
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date non disponible';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'reviewing': 'bg-blue-100 text-blue-800 border-blue-200',
      'accepted': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'in_progress': 'bg-purple-100 text-purple-800 border-purple-200',
      'completed': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const isImageFile = (fileName: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
  };

  // Fonction pour obtenir l'URL d'affichage selon le type de fichier
  const getFileUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/`;
    } else {
      return file.url || file.file_url || file.file || '';
    }
  };

  // Fonction pour obtenir l'URL de téléchargement
  const getDownloadUrl = (file: any): string => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';
    
    if (file.type === 'pdf' && file.pdf_id) {
      return `${API_BASE_URL}/api/pdf/${file.pdf_id}/download/`;
    } else {
      return file.url || file.file_url || file.file || '';
    }
  };

  const handleViewFile = (file: any, fileName: string) => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    window.open(fileUrl, '_blank');
  };

  const handleDownloadFile = (file: any, fileName: string) => {
    const fileUrl = getDownloadUrl(file);
    if (!fileUrl) {
      alert('URL du fichier non disponible');
      return;
    }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'fichier';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/submitted-projects/all/');
      const data = response.data;
      
      if (data.success) {
        setProjects(data.projects);
      } else {
        setError('Erreur lors du chargement des projets soumis');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des projets soumis:', err);
      setError('Erreur lors du chargement des projets soumis');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProjects = async () => {
    try {
      setRefreshing(true);
      await loadProjects();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStatusChange = async (projectId: number, newStatus: string) => {
    try {
      setUpdatingId(projectId);
      
      const response = await api.put(`/api/submitted-projects/${projectId}/update/`, {
        status: newStatus
      });

      const data = response.data;
      
      if (data.success) {
        // Mettre à jour le projet dans la liste
        setProjects(prev => prev.map(p => 
          p.id === projectId ? data.project : p
        ));
      } else {
        alert(data.error || 'Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des projets soumis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FolderOpen className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshProjects}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête avec bouton actualiser */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Projets Personnels Soumis</h2>
          <p className="text-gray-600">
            Gérez les projets soumis par les utilisateurs
          </p>
        </div>
        <button
          onClick={refreshProjects}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FolderOpen className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <AlertCircle className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En examen</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'reviewing').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Acceptés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {projects.filter(p => p.status === 'accepted').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des projets en grille */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
            {/* En-tête de la carte */}
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4">
              <h3 className="text-lg font-bold text-white line-clamp-2">{project.title}</h3>
            </div>

            {/* Contenu de la carte */}
            <div className="p-5">
              {/* Sélecteur de statut */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase">
                  Statut du projet
                </label>
                <select
                  value={project.status}
                  onChange={(e) => handleStatusChange(project.id, e.target.value)}
                  disabled={updatingId === project.id}
                  className={`w-full px-3 py-2 border-2 rounded-lg font-medium text-sm transition-colors ${
                    getStatusBadgeColor(project.status)
                  } ${updatingId === project.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                >
                  <option value="pending">⏳ En attente</option>
                  <option value="reviewing">👁️ En examen</option>
                  <option value="accepted">✅ Accepté</option>
                  <option value="rejected">❌ Rejeté</option>
                  <option value="in_progress">🔨 En cours</option>
                  <option value="completed">✔️ Terminé</option>
                </select>
              </div>

              <p className="text-gray-700 text-sm mb-4 line-clamp-3">{project.description}</p>
              
              {/* Informations client */}
              <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold text-gray-900">{project.user_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-indigo-600" />
                    <span className="text-gray-600">{project.user_email}</span>
                  </div>
                </div>
              </div>
              
              {/* Message */}
              {project.message && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <p className="text-xs font-semibold text-blue-900 mb-1 uppercase">💬 Message du client</p>
                  <p className="text-sm text-blue-800 line-clamp-2">{project.message}</p>
                </div>
              )}

              {/* Fichiers joints */}
              {project.files && project.files.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 uppercase flex items-center gap-1 mb-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    Fichiers joints ({project.files.length})
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {project.files.slice(0, 4).map((file: any, index: number) => {
                      const fileName = file.file_name || file.name || `Fichier ${index + 1}`;
                      const fileUrl = file.file_url || file.file || '';
                      const isImage = isImageFile(fileName);
                      
                      return (
                        <div 
                          key={index}
                          onClick={() => handleViewFile(fileUrl, fileName)}
                          className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-400 cursor-pointer transition-all duration-200 hover:shadow-md overflow-hidden"
                          style={{ aspectRatio: '1' }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center p-2">
                            {isImage ? (
                              <img 
                                src={fileUrl} 
                                alt={fileName}
                                className="w-full h-full object-cover rounded"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <File className="w-8 h-8 text-purple-400" />
                            )}
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(fileUrl, fileName);
                            }}
                            className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            title="Télécharger"
                          >
                            <Download className="w-3 h-3 text-purple-600" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {project.files.length > 4 && (
                    <p className="text-xs text-gray-500 text-center mt-2">+ {project.files.length - 4} autre(s) fichier(s)</p>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(project.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="col-span-full text-center py-12">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun projet soumis</h3>
            <p className="text-gray-600">
              Les projets soumis par les utilisateurs apparaîtront ici.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmittedProjectsAdminTab;
